import type { GetAdminV1StoresStoreIdSectionsIdPreviewParams } from '@nucleus-commerce/api-client/admin';

import type { BlockListParams, BlockPreviewParams, SectionListParams } from '../api/content-api';

/** Blocks are keyed by store, or under `org` for the organisation's shared blocks. */
const blockRoot = (storeId: string | null) =>
  storeId === null ? (['org', 'blocks'] as const) : (['stores', storeId, 'blocks'] as const);

export const contentKeys = {
  blocks: blockRoot,
  blockLists: (storeId: string | null) => [...blockRoot(storeId), 'list'] as const,
  blockList: (storeId: string | null, params: BlockListParams) =>
    [...blockRoot(storeId), 'list', params] as const,
  blockDetails: (storeId: string | null) => [...blockRoot(storeId), 'detail'] as const,
  blockDetail: (storeId: string | null, id: string) =>
    [...blockRoot(storeId), 'detail', id] as const,
  /** `content` is the resolvable part of the body only (see useBlockPreview), as stable JSON. */
  blockPreview: (storeId: string | null, params: BlockPreviewParams, content: string) =>
    [...blockRoot(storeId), 'preview', params, content] as const,

  sections: (storeId: string) => ['stores', storeId, 'sections'] as const,
  sectionLists: (storeId: string) => [...contentKeys.sections(storeId), 'list'] as const,
  sectionList: (storeId: string, params: SectionListParams) =>
    [...contentKeys.sectionLists(storeId), params] as const,
  sectionDetails: (storeId: string) => [...contentKeys.sections(storeId), 'detail'] as const,
  sectionDetail: (storeId: string, id: string) =>
    [...contentKeys.sectionDetails(storeId), id] as const,
  sectionPreview: (
    storeId: string,
    id: string,
    params: GetAdminV1StoresStoreIdSectionsIdPreviewParams,
  ) => [...contentKeys.sectionDetail(storeId, id), 'preview', params] as const,
};

/** Every block list of every store: a shared block shows up in store lists with scope=all. */
export const isAnyBlockList = (queryKey: readonly unknown[]) =>
  (queryKey[0] === 'org' && queryKey[1] === 'blocks') ||
  (queryKey[0] === 'stores' && queryKey[2] === 'blocks');
